import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook that tracks unread inbound SMS count for the current agent.
 * Unread = communications where direction='inbound', channel='sms', read_at IS NULL.
 *
 * Returns:
 * - unreadCount: number of unread inbound messages
 * - markConversationAsRead(leadId): marks all inbound messages for a lead as read
 * - refresh(): manually re-fetch the count
 */
export function useUnreadMessages() {
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      // Count inbound SMS where read_at is null
      const { count, error } = await supabase
        .from("communications")
        .select("id", { count: "exact", head: true })
        .eq("agent_id", user.user.id)
        .eq("channel", "sms")
        .eq("direction", "inbound")
        .is("read_at", null);

      if (error) {
        // Column may not exist yet (migration pending) — silently ignore
        if (error.message?.includes("read_at")) {
          setUnreadCount(0);
          return;
        }
        console.error("[useUnreadMessages] Error fetching count:", error);
        return;
      }

      setUnreadCount(count ?? 0);
    } catch (err) {
      console.error("[useUnreadMessages] Unexpected error:", err);
    }
  }, []);

  // Mark all inbound messages for a specific lead as read
  const markConversationAsRead = useCallback(async (leadId: string) => {
    try {
      const { error } = await supabase
        .from("communications")
        .update({ read_at: new Date().toISOString() })
        .eq("lead_id", leadId)
        .eq("channel", "sms")
        .eq("direction", "inbound")
        .is("read_at", null);

      if (error) {
        // Column may not exist yet — silently ignore
        if (error.message?.includes("read_at")) return;
        console.error("[useUnreadMessages] Error marking as read:", error);
        return;
      }

      // Optimistically decrement — or just re-fetch for accuracy
      await fetchUnreadCount();
    } catch (err) {
      console.error("[useUnreadMessages] Unexpected error:", err);
    }
  }, [fetchUnreadCount]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Realtime: refresh count on any communications change
  useEffect(() => {
    const channel = supabase
      .channel("unread-messages-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "communications" },
        () => {
          fetchUnreadCount();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    markConversationAsRead,
    refresh: fetchUnreadCount,
  };
}
